import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { useCampaigns, useCreateCampaign } from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label } from '@/components/ui/input';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { fmtDate } from '@/lib/format';

export function CampaignsPage() {
  const { data, isLoading } = useCampaigns();
  const create = useCreateCampaign();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({ name: name.trim(), objective: objective.trim() });
      toast.success('Campaign created.');
      setName('');
      setObjective('');
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Group your ad bookings into campaigns."
        action={
          <Button onClick={() => setOpen((v) => !v)}>
            <Plus className="h-4 w-4" /> New campaign
          </Button>
        }
      />

      {open && (
        <Card className="mb-4">
          <CardBody>
            <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div>
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Dashain Sale 2026" />
              </div>
              <div>
                <Label htmlFor="c-obj">Objective (optional)</Label>
                <Input id="c-obj" value={objective} onChange={(e) => setObjective(e.target.value)} placeholder="Awareness / traffic" />
              </div>
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6">
              <Spinner label="Loading campaigns…" />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Objective</Th>
                  <Th>Bookings</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {(data?.results.length ?? 0) === 0 && <EmptyRow colSpan={6}>No campaigns yet.</EmptyRow>}
                {data?.results.map((c) => (
                  <tr key={c.id}>
                    <Td className="font-medium">{c.name}</Td>
                    <Td className="text-muted-foreground">{c.objective || '—'}</Td>
                    <Td>{c.bookings_count}</Td>
                    <Td>
                      <Badge status={c.status}>{c.status_display}</Badge>
                    </Td>
                    <Td className="text-muted-foreground">{fmtDate(c.created_at)}</Td>
                    <Td className="text-right">
                      <Link to={`/campaigns/${c.id}`}>
                        <Button variant="outline" size="sm">
                          Open
                        </Button>
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </>
  );
}
